import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { encrypt, getWebToken, getUserToken, saveUserToken, clearUserStorage, logAuthEvent } from "../utils/tokenUtils";
import { ITokenData } from "./ITokenData";
import { ILoginInfo } from "../models/ILoginInfo";

export const S3_IMAGE_BASE_URL = "https://mutfacik.s3.eu-central-1.amazonaws.com/";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://service.mutfacikservices.com/api/";
const WEB_TOKEN = process.env.REACT_APP_WEB_TOKEN || "eyJhbGciOiJodHRwOi8vd3d3LnczLm9yZy8yMDAxLzA0L3htbGRzaWctbW9yZSNobWFjLXNoYTI1NiIsInR5cCI6IkpXVCJ9.eyJuYW1lIjoibXV0ZmFjaWt3ZWJzZXJ2aWNlIiwidHlwIjoiIiwibmJmIjoxNzI1MjExOTI4LCJleHAiOjE3MzAzOTU5MjgsImlzcyI6IllvdXJJc3N1ZXIiLCJhdWQiOiJZb3VyQXVkaWVuY2UifQ.p2Yi5e-CJklA2swJc-DT7jfjbAJpOyhw78xiEb2BUFc";

export const configWithoutToken = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    token: WEB_TOKEN,
  },
});

/* ------------------------------------------------------------------ */
/* Uygulama (web) token'ı                                              */
/* ------------------------------------------------------------------ */
/**
 * configWithWebToken'ın her isteğe eklediği uygulama-düzeyi bearer token.
 * "MA" adıyla localStorage'da tutulur; eksikse veya süresi dolmuşsa yeniden
 * alınır. Bu, sadece login/OTP/şehir listesi gibi çağrılar için değil,
 * aşağıdaki runRefresh (Auth/RefreshToken) için de gerekli — o da
 * configWithWebToken üzerinden gidiyor. Token hiç oluşmamışsa (temiz kurulum,
 * localStorage'ı temizlenmiş cihaz) refresh isteği kendisi 401 döner ve
 * kullanıcı gereksiz yere oturumdan atılır.
 */
let webTokenPromise: Promise<void> | null = null;

export const ensureWebToken = (): Promise<void> => {
  const existing = getWebToken();
  if (existing) {
    const hoursLeft = (new Date(existing.expiration).getTime() - Date.now()) / (1000 * 60 * 60);
    if (hoursLeft > 0) return Promise.resolve();
  }

  if (!webTokenPromise) {
    webTokenPromise = configWithoutToken
      .post<ILoginInfo>("Auth/Token", {
        email: "test123998@gmail.com",
        password: "Mutfacik2024",
        deviceId: "",
      })
      .then((res) => {
        const tokenData = {
          token: res.data.token,
          expiration: res.data.expiration,
          secretKey: res.data.secretKey,
          userId: res.data.userId,
        };
        localStorage.setItem("MA", encrypt(JSON.stringify(tokenData)));
      })
      .catch(() => {})
      .finally(() => {
        webTokenPromise = null;
      });
  }
  return webTokenPromise;
};

// Uygulama açılır açılmaz tetikle — gerçek istekler (login, şehir listesi,
// refresh) genelde bunun tamamlanmasını beklemeden önce hazır bulur.
ensureWebToken();

export const configWithWebToken = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

configWithWebToken.interceptors.request.use((config) => {
  const tokenObj = getWebToken();
  if (tokenObj?.token) {
    config.headers.Authorization = `Bearer ${tokenObj.token}`;
  }
  return config;
});

export const configWithUserToken = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    "accept": "*/*",
    "Content-Type": "application/json",
  },
});

configWithUserToken.interceptors.request.use((config) => {
  const tokenObj = getUserToken();
  if (!tokenObj?.token) {
    // Gercek axios iptali: axios.isCancel() ile ayirt edilebilir olmali,
    // aksi halde yakalanmayan duz nesne dev overlay'de "[object Object]" olarak patliyor.
    return Promise.reject(new axios.CanceledError("no_user_token"));
  }
  config.headers.Authorization = `Bearer ${tokenObj.token}`;
  return config;
});

export const configWithUserFileToken = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

configWithUserFileToken.interceptors.request.use((config) => {
  const tokenObj = getUserToken();
  if (!tokenObj?.token) {
    return Promise.reject(new axios.CanceledError("no_user_token"));
  }
  config.headers.Authorization = `Bearer ${tokenObj.token}`;
  return config;
});

/* ------------------------------------------------------------------ */
/* Kullanıcı token'ı yenileme                                          */
/* ------------------------------------------------------------------ */
/**
 * Kullanıcı token'ı süresi dolunca (401) oturumu direkt kapatmak yerine önce
 * refreshToken ile sessiz bir yenileme dener; kullanıcı hiçbir şey fark etmez.
 *
 * Önemli iki kural:
 *  1) Yenileme SADECE sunucu refresh token'ı açıkça reddettiğinde (400/401/403)
 *     oturumu kapatır. 429/5xx/timeout gibi geçici hatalarda oturuma dokunulmaz;
 *     aksi halde geçici bir hata kullanıcıyı dışarı atıp sayfayı sürekli
 *     yeniden yükleme döngüsüne sokuyordu.
 *  2) Az önce yenilendiyse tekrar yenilenmez. Paralel isteklerin geç gelen
 *     401'leri, rotate edilmiş refresh token'ı ikinci kez harcayıp kullanıcıyı
 *     dışarı atıyordu (hızlı gezinince ortaya çıkan hata).
 */
type RefreshOutcome =
  | { status: "ok"; data: ITokenData }
  | { status: "rejected" }
  | { status: "unavailable" };

const REFRESH_REUSE_MS = 10000;

let refreshPromise: Promise<RefreshOutcome> | null = null;
let lastRefreshAt = 0;
let sessionExpiredHandled = false;

// Login olunca flag'i sıfırla (circular dependency olmadan event ile)
window.addEventListener("user-logged-in", () => {
  sessionExpiredHandled = false;
});

const runRefresh = async (current: ITokenData): Promise<RefreshOutcome> => {
  try {
    // Auth/RefreshToken de configWithWebToken üzerinden gidiyor — uygulama
    // token'ı yoksa istek kendisi 401 döner ve kullanıcı gereksiz atılır.
    await ensureWebToken();
    const res = await configWithWebToken.post<ITokenData>("Auth/RefreshToken", {
      refreshToken: current.refreshToken,
    });
    // Sunucu alanların bir kısmını döndürmezse mevcut değerleri koru:
    // secretKey kaybolursa token localStorage'a hiç yazılamaz.
    const merged: ITokenData = {
      ...current,
      ...res.data,
      secretKey: res.data.secretKey || current.secretKey,
      userId: res.data.userId || current.userId,
      refreshToken: res.data.refreshToken || current.refreshToken,
    };
    saveUserToken(merged);
    lastRefreshAt = Date.now();
    return { status: "ok", data: merged };
  } catch (err) {
    const status = (err as AxiosError).response?.status;
    logAuthEvent("refresh-failed", {
      status: status ?? "network/timeout",
      response: (err as AxiosError).response?.data,
      refreshTail: current.refreshToken?.slice(-6),
    });
    if (status === 400 || status === 401 || status === 403) {
      // Başka bir sekme aynı refresh token'ı bizden önce harcadıysa sunucu bizi
      // reddeder ama o sekme yeni token'ı ortak localStorage'a yazmıştır.
      // Bu durumda oturum geçerli — kapatmak iki sekmeyi birden dışarı atıyordu.
      const latest = getUserToken();
      if (latest?.token && latest.refreshToken && latest.refreshToken !== current.refreshToken) {
        lastRefreshAt = Date.now();
        return { status: "ok", data: latest };
      }
      return { status: "rejected" };
    }
    return { status: "unavailable" };
  }
};

/**
 * Sekmeler arası kilit: aynı tarayıcıda açık birden fazla sekme localStorage'daki
 * aynı refresh token'ı paylaşır. Kilit sayesinde biri yenilerken diğeri bekler,
 * sonra yenilenmiş token'ı localStorage'dan okuyup tekrar refresh etmez.
 * Web Locks desteklenmiyorsa runRefresh içindeki kontrol yedek görevi görür.
 */
const withRefreshLock = (fn: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> =>
  "locks" in navigator && navigator.locks
    ? navigator.locks.request("mutfacik-user-token-refresh", fn)
    : fn();

const refreshAccessToken = (): Promise<RefreshOutcome> => {
  const current = getUserToken();
  if (!current?.token || !current.refreshToken) {
    logAuthEvent("refresh-skipped-no-refresh-token", { hasToken: !!current?.token });
    return Promise.resolve({ status: "rejected" });
  }

  if (refreshPromise) return refreshPromise;

  if (Date.now() - lastRefreshAt < REFRESH_REUSE_MS) {
    return Promise.resolve({ status: "ok", data: current });
  }

  refreshPromise = withRefreshLock(async (): Promise<RefreshOutcome> => {
    const latest = getUserToken();
    if (!latest?.token || !latest.refreshToken) return { status: "rejected" };
    // Kilidi beklerken başka sekme yeniledi — onun token'ını kullan
    if (latest.refreshToken !== current.refreshToken) {
      lastRefreshAt = Date.now();
      return { status: "ok", data: latest };
    }
    return runRefresh(latest);
  }).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
};

const handleSessionExpired = (triggerUrl?: string) => {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  logAuthEvent("session-expired-logout", { triggerUrl });
  clearUserStorage();
  window.dispatchEvent(new CustomEvent("session-expired"));

  // "Beni hatırla" otomatik girişi açıkken arka arkaya yönlendirme, sayfayı
  // sürekli yenileme döngüsüne sokabiliyor. Yakın zamanda yönlendirdiysek
  // sayfayı değiştirmeden sadece login modal'ını aç.
  const lastRedirect = Number(sessionStorage.getItem("sessionExpiredAt") ?? 0);
  if (Date.now() - lastRedirect < 30000) {
    window.dispatchEvent(new CustomEvent("show-login"));
    return;
  }
  sessionStorage.setItem("sessionExpiredAt", String(Date.now()));

  if (window.location.pathname === "/") {
    window.dispatchEvent(new CustomEvent("show-login"));
  } else {
    window.location.href = "/?showLogin=1";
  }
};

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean };

const attachRefreshOnUnauthorized = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as RetriableRequestConfig | undefined;

      if (error.response?.status !== 401 || !original || original._retriedAfterRefresh) {
        return Promise.reject(error);
      }

      // Token yoksa kullanıcı zaten çıkış yapmış — refresh deneme, döngüye girme
      const stored = getUserToken();
      if (!stored?.token) {
        return Promise.reject(error);
      }

      original._retriedAfterRefresh = true;

      // İstek eski token'la gitmiş ama token o arada (başka sekmede/istekte) yenilenmiş:
      // refresh harcamadan tekrar dene — request interceptor güncel token'ı ekler.
      const sentToken = String(original.headers?.Authorization ?? "").replace(/^Bearer\s+/i, "");
      if (sentToken && sentToken !== stored.token) {
        return instance(original);
      }

      logAuthEvent("401-refreshing", {
        url: original.url,
        response: error.response?.headers?.["www-authenticate"],
      });
      const outcome = await refreshAccessToken();

      if (outcome.status === "ok") {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${outcome.data.token}`;
        return instance(original);
      }

      // "unavailable" → geçici hata; oturumu kapatma, sadece bu istek başarısız olsun.
      if (outcome.status === "rejected") {
        handleSessionExpired(original.url);
      }
      return Promise.reject(error);
    }
  );
};

attachRefreshOnUnauthorized(configWithUserToken);
attachRefreshOnUnauthorized(configWithUserFileToken);

export const config = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
});
